import { setCurrentTheme } from '@redux/actions';
import { ThemeInterface, User } from '@redux/prop-types';
import {
  isSignedInSelector,
  themeSelector,
  userSelector
} from '@redux/selectors';
import { AccessLevel } from '@utils/enums/access-levels';
import { ThemeEnum } from '@utils/enums/theme-enum';
import { useUserAccess } from '@utils/hooks/use-user-access';
import { mustUserDoPlacementTest } from '@utils/placement-test';
import {
  getCurrentTheme,
  getMatchMediaDark,
  saveCurrentTheme
} from '@utils/theme';
import { navigate } from 'gatsby';
import React, { ReactNode, useEffect } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

const mapStateToProps = createSelector(
  isSignedInSelector,
  userSelector,
  themeSelector,
  (isSignedIn, user: User, theme: ThemeInterface) => ({
    isSignedIn,
    user,
    theme
  })
);

const mapDispatchToProps = {
  setCurrentTheme
};

interface LearnLayout {
  readonly children: ReactNode;
  readonly user: User;
  readonly isSignedIn: boolean;
  readonly theme: ThemeInterface;
}

function LearnLayout({
  children,
  user,
  isSignedIn,
  theme,
  setCurrentTheme
}: LearnLayout & typeof mapDispatchToProps) {
  const isFirstRender = React.useRef(true);
  const userAccess = useUserAccess(user);

  useEffect(() => {
    setCurrentTheme(getCurrentTheme());
  }, [setCurrentTheme]);

  useEffect(() => {
    const currentTheme = theme.currentTheme;
    document.documentElement.classList.add(currentTheme);
    saveCurrentTheme(currentTheme);
    return () => {
      document.documentElement.classList.remove(currentTheme);
    };
  }, [theme.currentTheme]);

  useEffect(() => {
    const media = getMatchMediaDark();
    const listener = (e: MediaQueryListEvent) => {
      setCurrentTheme(e.matches ? ThemeEnum.DARK : ThemeEnum.LIGTH);
    };
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [setCurrentTheme]);

  useEffect(() => {
    if (isSignedIn && user.access == 'limited_nacional' && user.source) {
      void navigate('/learn');
      return;
    }

    if (isSignedIn && isFirstRender.current) {
      if (
        (user.access === AccessLevel.LIMITED_NACIONAL ||
          user.access === AccessLevel.COMPLETE_NACIONAL) &&
        mustUserDoPlacementTest(user) &&
        !user.isBlockedByExpiration
      ) {
        void navigate('/placement-test');
      }

      if (!userAccess.verifyClassGroupStarted && !user.isBlockedByExpiration) {
        void navigate('/learn');
      }

      isFirstRender.current = false;
    }
  }, [user, isSignedIn, userAccess.verifyClassGroupStarted]);

  return <>{children}</>;
}
export default connect(mapStateToProps, mapDispatchToProps)(LearnLayout);
